import { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
} from '@mui/material';
import { Box } from '@mui/material';
import { locationTypes } from '../../constants/statusOptions';
import { createLocation } from '../../services/locationService';
import { getProjects } from '../../services/projectService';

interface ProjectOption {
  ProjectKey: string;
  ProjectId: string;
  ProjectName: string;
}

interface NewLocationManageDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const NewLocationManageDialog = ({ open, onClose, onCreated }: NewLocationManageDialogProps) => {
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [locationId, setLocationId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [nameEditedByUser, setNameEditedByUser] = useState(false);
  const [locationType, setLocationType] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      getProjects({ pageSize: 9999 })
        .then((r) => setProjects(r.data.map((p) => ({
          ProjectKey: p.ProjectKey,
          ProjectId: p.ProjectId,
          ProjectName: p.ProjectName,
        }))))
        .catch(() => {});
    }
  }, [open]);

  const isValid = !!selectedProject && locationId.trim() !== '' && locationName.trim() !== '' && locationType !== '';

  const handleSubmit = async () => {
    if (!isValid || !selectedProject) return;
    setSaving(true);
    setError('');
    try {
      await createLocation(selectedProject.ProjectKey, {
        LocationId: locationId.trim(),
        LocationName: locationName.trim(),
        LocationType: locationType,
      });
      handleClose();
      onCreated();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to create location.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedProject(null);
    setLocationId('');
    setLocationName('');
    setNameEditedByUser(false);
    setLocationType('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>New Location</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <Autocomplete
          size="small"
          options={projects}
          getOptionLabel={(opt) => `${opt.ProjectId} - ${opt.ProjectName}`}
          value={selectedProject}
          onChange={(_e, val) => setSelectedProject(val)}
          renderInput={(params) => (
            <TextField {...params} label="Project" required placeholder="Select a project..." />
          )}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField size="small" label="Location ID" required value={locationId}
            onChange={(e) => {
              const v = e.target.value;
              setLocationId(v);
              // Auto-mirror to Name until the user manually edits Name (TASK-004)
              if (!nameEditedByUser) setLocationName(v);
            }} />
          <TextField size="small" label="Location Name" required value={locationName}
            onChange={(e) => { setLocationName(e.target.value); setNameEditedByUser(true); }} />
        </Box>
        <TextField size="small" select label="Location Type" required value={locationType}
          onChange={(e) => setLocationType(e.target.value)}>
          {locationTypes.map((lt) => <MenuItem key={lt} value={lt}>{lt}</MenuItem>)}
        </TextField>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!isValid || saving}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}>
          {saving ? 'Creating...' : 'Create Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
